import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Task } from '../../../api/model';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import OkModal from '../../modules/OkModal';
import PageNavi from '../../modules/PageNavi';

interface Props {
  tasks: Task[];
  taskStatus: number;
}
const N_DATA_PER_PAGE = 50;

// 一覧のソートキー
type SortKey =
  | 'updated_at'
  | 'sales_my'
  | 'model_name'
  | 'destination_name'
  | 'production_division_name'
  | 'production_no'
  | 'title'
  | 'production_status_name'
  | 'next_task'
  | 'comment'
  | 'due_date'
  | 'create_user_name'
  | 'person_in_charge_user_name'
  | 'remaining_days'
  | 'chapter_updated_on'
  | 'release_on'
  | 'completion_rate'
  | 'production_title';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

function TaskTable({ tasks, taskStatus }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Tasks.CurrentPage', 1));
  const [comment, setComment] = useState<string | null>(null);

  // ソート情報
  const DEFAULT_SORT_KEY = taskStatus === 1 ? 'updated_at' : 'release_on';

  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Tasks.SortInfo', { key: DEFAULT_SORT_KEY as SortKey, isDisc: true })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedTasks = useMemo(
    () =>
      [...tasks].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, tasks]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Tasks.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Tasks.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (tasks != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [tasks]);

  // 現在のページに表示する一覧
  const filteredTasks = useMemo(
    () => sortedTasks.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedTasks]
  );

  return (
    <div>
      <div className="scroll" style={{ marginBottom: '40px' }}>
        <table className="table1">
          <tbody>
            <tr>
              <th>link</th>
              <SortableTh label={t('更新日')} onClick={() => changeSortKey('updated_at')} />
              <SortableTh label={t('M/Y')} onClick={() => changeSortKey('sales_my')} />
              <SortableTh label={t('機種名')} onClick={() => changeSortKey('model_name')} />
              <SortableTh label={t('仕向')} onClick={() => changeSortKey('destination_name')} />
              <SortableTh label={t('資料区分')} onClick={() => changeSortKey('production_division_name')} />
              <SortableTh label={t('資料管理No')} onClick={() => changeSortKey('production_no')} />
              <SortableTh label={t('件名')} onClick={() => changeSortKey('title')} />
              <SortableTh label={t('ステータス')} onClick={() => changeSortKey('production_status_name')} />
              <SortableTh label={t('補足')} onClick={() => changeSortKey('comment')} />
              <SortableTh label={t('次のタスク')} onClick={() => changeSortKey('next_task')} />
              <SortableTh label={t('期限')} onClick={() => changeSortKey('due_date')} />
              <SortableTh label={t('作成者')} onClick={() => changeSortKey('create_user_name')} />
              <SortableTh label={t('担当者')} onClick={() => changeSortKey('person_in_charge_user_name')} />
              <SortableTh label={t('残日数')} onClick={() => changeSortKey('remaining_days')} />
              <SortableTh label={t('原稿更新日')} onClick={() => changeSortKey('chapter_updated_on')} />
              <SortableTh label={t('完成日')} onClick={() => changeSortKey('release_on')} />
              <SortableTh label={t('原稿完成率')} onClick={() => changeSortKey('completion_rate')} />
              <SortableTh label={t('資料名')} onClick={() => changeSortKey('production_title')} />
            </tr>
            {filteredTasks.map((task) => (
              <tr key={task.task_id}>
                <td>
                  {task.confirmation_request_id !== null && (
                    <Link
                      to={`/${language}/productions/edit/${Number(
                        task.production_id
                      )}/confirmation-request/edit/${Number(task.confirmation_request_id)}?mode=home`}
                      target="_blank"
                    >
                      <i className="fa-solid fa-link" />
                    </Link>
                  )}
                </td>
                <td>{task.updated_at}</td>
                <td>{task.sales_my}</td>
                <td>{task.model_name}</td>
                <td>{task.destination_name}</td>
                <td>{task.production_division_name}</td>
                <td>{task.production_no}</td>
                <td><div>{task.title}</div></td>
                <td>{task.production_status_name}</td>
                <td>
                  <div onClick={() => task.comment && setComment(task.comment)}>{task.comment}</div>
                </td>
                <td>{task.next_task}</td>
                <td>{task.due_date}</td>
                <td>{task.create_user_name}</td>
                <td>
                  <div>{task.person_in_charge_user_name}</div>
                </td>
                <td>{task.remaining_days}</td>
                <td>{task.chapter_updated_on}</td>
                <td>{task.release_on}</td>
                <td>{task.completion_rate}</td>
                <td>{task.production_title}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(tasks.length / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />

      {comment && <OkModal onClickOk={() => setComment(null)}>{comment}</OkModal>}
    </div>
  );
}

export { TaskTable };
